import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n/I18nContext";
import LangSwitch from "@/components/LangSwitch";
import ThemeSwitch from "@/components/ThemeSwitch";
import Marque from "@/components/Marque";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const CONTACT = "quotes@roofborder.com";

/** Choix du mot de passe au premier accès, depuis le lien signé. */
export default function Activer() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const { lang } = useI18n();
  const fr = lang !== "en";
  const params = new URLSearchParams(window.location.search);
  const uid = params.get("u") || "";
  const jeton = params.get("t") || "";

  const [info, setInfo] = useState(null);
  const [invalide, setInvalide] = useState("");
  const [mdp, setMdp] = useState("");
  const [envoi, setEnvoi] = useState(false);

  useEffect(() => {
    api.get("/compte/activation", { params: { u: uid, t: jeton } })
      .then(({ data }) => setInfo(data))
      .catch((err) => setInvalide(formatApiErrorDetail(err.response?.data?.detail) || "?"));
  }, [uid, jeton]);

  const soumettre = async (e) => {
    e.preventDefault();
    setEnvoi(true);
    try {
      const { data } = await api.post("/compte/activation", { user_id: uid, t: jeton, password: mdp });
      setUser(data.user);
      toast.success(fr ? "Compte activé" : "Account activated");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || (fr ? "Échec" : "Failed"));
      setEnvoi(false);
    }
  };

  return (
    <div className="min-h-screen bg-app text-ink">
      <header className="border-b border-line">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Marque taille="md" />
          <div className="flex items-center gap-3">
            <ThemeSwitch />
            <LangSwitch />
          </div>
        </div>
      </header>

      <main className="max-w-md mx-auto px-6 py-20">
        {invalide && (
          <div>
            <h1 className="font-heading text-2xl font-extrabold tracking-tighter mb-3">
              {fr ? "Ce lien n'est plus valide" : "This link is no longer valid"}
            </h1>
            <p className="text-ink-soft">
              {fr
                ? "Il a peut-être déjà servi : dans ce cas votre mot de passe est défini et vous pouvez vous connecter normalement."
                : "It may already have been used: if so your password is set and you can simply sign in."}
            </p>
            <Link to="/login">
              <Button className="mt-6 bg-brand hover:bg-brand/90 text-white rounded-md h-11 px-6 font-bold">
                {fr ? "Aller à la connexion" : "Go to sign in"}
              </Button>
            </Link>
            <p className="text-sm text-ink-faint mt-6">
              {fr ? "Besoin d'aide ? " : "Need help? "}
              <a href={`mailto:${CONTACT}`} className="text-brand-ink hover:underline">{CONTACT}</a>
            </p>
          </div>
        )}

        {!invalide && !info && (
          <div className="flex justify-center py-10">
            <Loader2 className="h-7 w-7 animate-spin text-brand" />
          </div>
        )}

        {info && (
          <form onSubmit={soumettre}>
            <h1 className="font-heading text-3xl font-extrabold tracking-tighter leading-tight">
              {fr ? "Choisissez votre mot de passe" : "Choose your password"}
            </h1>
            <p className="mt-3 text-ink-soft">
              {fr ? "Compte : " : "Account: "}
              <strong className="text-ink">{info.email}</strong>
            </p>

            <div className="mt-8">
              <Label htmlFor="mdp">{fr ? "Mot de passe" : "Password"}</Label>
              <Input
                id="mdp"
                data-testid="mdp-activation"
                type="password"
                autoComplete="new-password"
                minLength={8}
                required
                value={mdp}
                onChange={(e) => setMdp(e.target.value)}
                className="mt-2 bg-field border-line-strong h-11"
              />
              <p className="text-xs text-ink-faint mt-2">
                {fr ? "8 caractères minimum." : "8 characters minimum."}
              </p>
            </div>

            <Button
              type="submit"
              disabled={envoi || mdp.length < 8}
              className="mt-6 w-full bg-brand hover:bg-brand/90 text-white rounded-md h-12 font-bold"
            >
              {envoi && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {fr ? "Activer mon compte" : "Activate my account"}
            </Button>
          </form>
        )}
      </main>
    </div>
  );
}
